// 그래프(실시간) 화면 — 4개 패널
// NOTE: tb_monthly_summary 사용 금지. 모든 데이터는 실시간 직접 계산.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
  ComposedChart, Line,
} from 'recharts';
import {
  fetchPortfolio, fetchPortfolioDetail,
  fetchProfitRate, fetchCashflow, fetchPlanRatio,
} from '../../api/graph';
import { fetchRealtimePrices } from '../../api/price';
import { fetchAccounts } from '../../api/accounts';
import PageLayout from '../../components/PageLayout';
import MultiSelectCombo from '../../components/common/MultiSelectCombo';
import './GraphPage.css';

const COLORS = [
  '#4a90e2', '#50c878', '#f5a623', '#e74c3c', '#9b59b6',
  '#1abc9c', '#e67e22', '#3498db', '#e91e63', '#607d8b',
];

// 예상수익 도넛 색상
const PROFIT_COLORS = ['#e74c3c', '#c0392b', '#e8735a', '#f5a623'];
const LOSS_COLORS   = ['#3498db', '#2980b9', '#5dade2', '#85c1e9'];

const fNum  = (v) => Number(v).toLocaleString('ko-KR');
const fRate = (v) => Number(v).toFixed(2);
const fY    = (v) => {
  const n = Math.abs(v);
  if (n >= 100000000) return `${(v / 100000000).toFixed(0)}억`;
  if (n >= 10000)     return `${(v / 10000).toFixed(0)}만`;
  return String(Math.round(v));
};

// ── 커스텀 툴팁 ────────────────────────────────────────────

const PieTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="gp-tooltip">
      <div className="gp-tooltip-title">{d.name}</div>
      <div>{fNum(d.amount)}원 ({fRate(d.ratio)}%)</div>
    </div>
  );
};

const ProfitPieTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const sign = d.profit_loss >= 0 ? '+' : '';
  return (
    <div className="gp-tooltip">
      <div className="gp-tooltip-title">{d.name}</div>
      <div style={{ color: d.is_profit ? '#cc0000' : '#0000cc' }}>
        {sign}{fNum(d.profit_loss)}원
      </div>
      <div style={{ color: '#888' }}>{fRate(d.ratio)}%</div>
    </div>
  );
};

const ProfitTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const rate = payload[0].value;
  return (
    <div className="gp-tooltip">
      <div className="gp-tooltip-title">{label}</div>
      <div style={{ color: rate >= 0 ? '#cc0000' : '#0000cc' }}>{fRate(rate)}%</div>
    </div>
  );
};

const CashflowTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="gp-tooltip">
      <div className="gp-tooltip-title">{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color }}>{p.name}: {fNum(p.value)}원</div>
      ))}
    </div>
  );
};

const RatioTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <div className="gp-tooltip">
      <div className="gp-tooltip-title">{label}</div>
      {d?.sector_name && <div style={{ color: '#888', fontSize: 11 }}>{d.sector_name}</div>}
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color }}>{p.name}: {fRate(p.value)}%</div>
      ))}
    </div>
  );
};

const CASHFLOW_PERIODS = [
  { months: 7,  label: '6개월' },
  { months: 13, label: '1년' },
  { months: 19, label: '1년6개월' },
  { months: 25, label: '2년' },
];

// ════════════════════════════════════════════════════════════

const GraphPage = ({ isActive }) => {
  // ── 조회 조건 ─────────────────────────────────────────────
  const [filterAccount,   setFilterAccount]   = useState([]);
  const [includeInterest, setIncludeInterest] = useState(false);
  const [cashflowMonths,  setCashflowMonths]  = useState(13);

  // ── 콤보 ─────────────────────────────────────────────────
  const [accounts, setAccounts] = useState([]);

  // ── 그래프 데이터 ─────────────────────────────────────────
  const [portfolioData, setPortfolioData] = useState(null);
  const [profitData,    setProfitData]    = useState(null);
  const [cashflowData,  setCashflowData]  = useState(null);
  const [planData,      setPlanData]      = useState(null);

  // ── 패널 내 토글 ──────────────────────────────────────────
  const [portfolioView, setPortfolioView] = useState('sector'); // sector | account | profit
  const [planView,      setPlanView]      = useState('sector');

  // ── 로딩/상태 ─────────────────────────────────────────────
  const [loading,      setLoading]      = useState(false);
  const [priceLoading, setPriceLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [priceSource,  setPriceSource]  = useState(null);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [message,      setMessage]      = useState({ text: '', type: '' });

  // ── 팝업 ─────────────────────────────────────────────────
  const [piePopup, setPiePopup] = useState({
    open: false, loading: false, type: 'sector', name: '', data: [],
  });

  const profitRawRef    = useRef([]);
  const lastPieClickRef = useRef({ time: 0, name: '' }); // 더블클릭 감지용
  const isLoadingRef    = useRef(false);

  const showMessage = (text, type = 'error') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 3000);
  };

  useEffect(() => {
    if (!isActive) return;
    fetchAccounts().then(setAccounts).catch(() => {});
  }, [isActive]);

  // 화면 진입 시 자동 조회
  useEffect(() => {
    if (!isActive) return;
    handleLoad();
  }, [isActive]); // eslint-disable-line react-hooks/exhaustive-deps

  // ESC로 팝업 닫기
  useEffect(() => {
    if (!piePopup.open) return;
    const handler = (e) => {
      if (e.key === 'Escape') setPiePopup(p => ({ ...p, open: false }));
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [piePopup.open]);

  // ── 수익률 실시간 갱신 ────────────────────────────────────

  const runRealtimeUpdate = useCallback(async (rawItems) => {
    const tickers = [...new Set(rawItems.map(d => d.ticker))];
    if (!tickers.length) return;
    setPriceLoading(true);
    try {
      const res = await fetchRealtimePrices(tickers);
      const priceMap = {};
      for (const p of res.data) priceMap[p.ticker] = p;

      const updated = rawItems.map(item => {
        const p = priceMap[item.ticker];
        if (!p || !p.current_price) return item;
        const newRate = item.avg_buy_price > 0
          ? parseFloat(((p.current_price - item.avg_buy_price) / item.avg_buy_price * 100).toFixed(2))
          : 0;
        return { ...item, profit_rate: newRate };
      });
      updated.sort((a, b) => b.profit_rate - a.profit_rate);

      setProfitData(prev => prev ? { ...prev, data: updated } : prev);
      setPriceSource(res.data.some(p => p.source === 'cached') ? 'mixed' : 'realtime');
    } catch {
      setPriceSource(null);
    } finally {
      setPriceLoading(false);
    }
  }, []);

  const handleManualRefresh = useCallback(async () => {
    if (isRefreshing || priceLoading || !profitRawRef.current.length) return;
    setIsRefreshing(true);
    try {
      await runRealtimeUpdate(profitRawRef.current);
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, priceLoading, runRealtimeUpdate]);

  // ── 현금흐름 단독 재조회 ──────────────────────────────────

  const loadCashflow = useCallback(async (acct, months) => {
    try {
      setCashflowData(await fetchCashflow(acct, months));
    } catch {}
  }, []);

  const handleCashflowMonthsChange = useCallback(async (m) => {
    setCashflowMonths(m);
    if (!isDataLoaded) return;
    await loadCashflow(filterAccount.join(','), m);
  }, [isDataLoaded, filterAccount, loadCashflow]);

  // ── 전체 조회 ─────────────────────────────────────────────

  const handleLoad = useCallback(async () => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    const acct = filterAccount.join(',');
    setLoading(true);
    setPriceSource(null);
    try {
      const [port, profit, cashflow, plan] = await Promise.all([
        fetchPortfolio(acct, includeInterest),
        fetchProfitRate(acct),
        fetchCashflow(acct, cashflowMonths),
        fetchPlanRatio(acct),
      ]);
      setPortfolioData(port);
      profitRawRef.current = profit.data ?? [];
      setProfitData(profit);
      setCashflowData(cashflow);
      setPlanData(plan);
      setIsDataLoaded(true);
      if (profit.data?.length) {
        await runRealtimeUpdate(profit.data);
      }
    } catch {
      showMessage('조회에 실패했습니다.');
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [filterAccount, includeInterest, cashflowMonths, runRealtimeUpdate]);

  // ── 현금성포함 체크 변경 → 포트폴리오만 재조회 ────────────

  const handleInterestChange = useCallback(async (checked) => {
    setIncludeInterest(checked);
    if (!isDataLoaded) return;
    const acct = filterAccount.join(',');
    try {
      const port = await fetchPortfolio(acct, checked);
      setPortfolioData(port);
    } catch {}
  }, [isDataLoaded, filterAccount]);

  // ── 도넛 클릭 → 더블클릭 감지 → 종목 목록 팝업 ─────────────
  // recharts <Pie onDoubleClick>은 네이티브 DOM 이벤트를 받아
  // 섹터 데이터(name 등)가 전달되지 않으므로, onClick으로 대체.

  const handlePieClick = useCallback((entryData) => {
    const now  = Date.now();
    const name = entryData?.name ?? '';
    const last = lastPieClickRef.current;

    if (now - last.time < 350 && last.name === name) {
      // ── 더블클릭 확정 ──────────────────────────────────────
      lastPieClickRef.current = { time: 0, name: '' };

      if (portfolioView === 'profit') {
        const allProfitItems = portfolioData?.by_profit ?? [];
        if (!allProfitItems.length) return;
        setPiePopup({ open: true, loading: false, type: 'profit', name: '', data: allProfitItems });
        return;
      }

      const segName = name || '미분류';
      setPiePopup({ open: true, loading: true, type: portfolioView, name: segName, data: [] });
      const acct = filterAccount.join(',');
      fetchPortfolioDetail(acct, includeInterest, portfolioView, segName)
        .then(res => setPiePopup(prev => ({ ...prev, loading: false, data: res.data ?? [] })))
        .catch(()  => setPiePopup(prev => ({ ...prev, loading: false })));
    } else {
      // ── 첫 번째 클릭 기록 ────────────────────────────────
      lastPieClickRef.current = { time: now, name };
    }
  }, [portfolioView, filterAccount, includeInterest, portfolioData]);

  // ── 시세 상태 표시 ────────────────────────────────────────

  const priceStatusEl = (priceLoading || isRefreshing) ? (
    <span className="gp-price-status loading">시세조회중…</span>
  ) : priceSource === 'realtime' ? (
    <span className="gp-price-status realtime">● 실시간</span>
  ) : priceSource === 'mixed' ? (
    <span className="gp-price-status mixed">● 혼합</span>
  ) : null;

  // ── 툴바 ─────────────────────────────────────────────────

  const toolbar = (
    <div className="gp-toolbar">
      <MultiSelectCombo
        options={accounts.map(a => ({ value: a.account_name, label: a.account_name }))}
        value={filterAccount}
        onChange={setFilterAccount}
        placeholder="전체"
      />
      <button className="btn btn-primary" onClick={handleLoad} disabled={loading}>
        {loading ? '조회중…' : '조회'}
      </button>
      {isDataLoaded && (
        <>
          <span className="gp-sep" />
          {priceStatusEl}
          <button
            className={`gp-refresh-btn${(isRefreshing || priceLoading) ? ' spinning' : ''}`}
            onClick={handleManualRefresh}
            disabled={isRefreshing || priceLoading || !profitRawRef.current.length}
            title="② 수익률 시세 갱신"
          >↻</button>
        </>
      )}
    </div>
  );

  // ════════════════════════════════════════════════════════
  // 패널 ①: 포트폴리오 구성 (도넛)
  //   - 섹터별 / 계좌별: 더블클릭으로 상세 팝업
  //   - 예상수익: 종목별 손익 도넛 (절대값 크기, 색상 구분)
  // ════════════════════════════════════════════════════════

  const renderPortfolio = () => {
    const isProfitMode = portfolioView === 'profit';

    let items = [];
    let pieDataKey = 'amount';
    let pieTooltip = <PieTooltip />;
    let pieLabel   = ({ name, ratio }) => `${name} ${fRate(ratio)}%`;

    if (isProfitMode) {
      items      = (portfolioData?.by_profit ?? []).filter(d => d.abs_amount > 0);
      pieDataKey = 'abs_amount';
      pieTooltip = <ProfitPieTooltip />;
      pieLabel   = ({ name, ratio }) => `${name} ${fRate(ratio)}%`;
    } else if (portfolioView === 'sector') {
      items = portfolioData?.by_sector ?? [];
    } else {
      items = portfolioData?.by_account ?? [];
    }

    const netProfit = portfolioData?.net_profit ?? 0;

    return (
      <div className="gp-panel">
        <div className="gp-panel-header">
          <span className="gp-panel-title">① 포트폴리오 구성</span>
          <div className="gp-panel-header-right">
            <label className={`gp-check-label gp-chk-sm${isProfitMode ? ' gp-disabled' : ''}`}>
              <input
                type="checkbox"
                checked={includeInterest}
                onChange={e => handleInterestChange(e.target.checked)}
                disabled={isProfitMode}
              />
              현금성포함
            </label>
            <span className="gp-vbar" />
            <div className="gp-toggle-group">
              <button
                className={`gp-toggle-btn ${portfolioView === 'sector' ? 'active' : ''}`}
                onClick={() => setPortfolioView('sector')}
              >섹터별</button>
              <button
                className={`gp-toggle-btn ${portfolioView === 'account' ? 'active' : ''}`}
                onClick={() => setPortfolioView('account')}
              >계좌별</button>
              <button
                className={`gp-toggle-btn ${portfolioView === 'profit' ? 'active' : ''}`}
                onClick={() => setPortfolioView('profit')}
              >예상수익액 비율</button>
            </div>
          </div>
        </div>
        <div className="gp-panel-body">
          {!portfolioData ? (
            <div className="gp-empty">조회 버튼을 클릭하세요.</div>
          ) : items.length === 0 ? (
            <div className="gp-empty">
              {isProfitMode ? '시세 데이터가 없습니다.' : '데이터가 없습니다.'}
            </div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={items}
                    dataKey={pieDataKey}
                    nameKey="name"
                    cx={isProfitMode ? "42%" : "50%"}
                    cy="50%"
                    innerRadius="38%"
                    outerRadius="62%"
                    paddingAngle={2}
                    label={pieLabel}
                    labelLine={true}
                    onClick={handlePieClick}
                    style={{ cursor: 'pointer' }}
                  >
                    {isProfitMode
                      ? items.map((d, i) => (
                          <Cell
                            key={i}
                            fill={d.is_profit
                              ? PROFIT_COLORS[i % PROFIT_COLORS.length]
                              : LOSS_COLORS[i % LOSS_COLORS.length]
                            }
                          />
                        ))
                      : items.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))
                    }
                  </Pie>
                  <Tooltip content={pieTooltip} />
                </PieChart>
              </ResponsiveContainer>
              {isProfitMode && (
                <div className="gp-donut-center" style={{ left: '42%' }}>
                  <div className="gp-donut-center-label">순예상손익</div>
                  <div
                    className="gp-donut-center-value"
                    style={{ color: netProfit >= 0 ? '#cc0000' : '#0000cc' }}
                  >
                    {netProfit >= 0 ? '+' : ''}{fNum(Math.round(netProfit))}원
                  </div>
                </div>
              )}
              {!isProfitMode && (
                <div className="gp-profit-legend">
                  <div style={{ color: '#bbb', fontSize: 11 }}>도넛 더블클릭 : 종목 상세 정보 확인</div>
                </div>
              )}
              {isProfitMode && items.length > 0 && (
                <div className="gp-profit-legend">
                  <div className="gp-profit-legend-sep" />
                  <div><span style={{ color: '#FF4444' }}>●</span> 빨강 계열 : 수익예상 종목</div>
                  <div><span style={{ color: '#4444FF' }}>●</span> 파랑 계열 : 손실예상 종목</div>
                  <div className="gp-profit-legend-note">도넛 크기는 예상손익 절대값 기준이며 합을 100%로 합니다.</div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  // ════════════════════════════════════════════════════════
  // 패널 ②: 종목별 수익률 (세로 막대 + 실시간 2단계)
  // ════════════════════════════════════════════════════════

  const renderProfitRate = () => {
    const data = profitData?.data ?? [];

    return (
      <div className="gp-panel">
        <div className="gp-panel-header">
          <span className="gp-panel-title">② 종목별 수익률(단순 수익률 기준)</span>
          {(priceLoading || isRefreshing) && (
            <span className="gp-panel-sub">시세 갱신 중…</span>
          )}
        </div>
        <div className="gp-panel-body">
          {!profitData ? (
            <div className="gp-empty">조회 버튼을 클릭하세요.</div>
          ) : data.length === 0 ? (
            <div className="gp-empty">미매도 종목이 없습니다.</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data}
                barCategoryGap="30%"
                margin={{ top: 8, right: 16, left: 0, bottom: data.length > 8 ? 50 : 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="stock_name"
                  tick={{
                    fontSize: 10,
                    angle: data.length > 6 ? -40 : 0,
                    textAnchor: data.length > 6 ? 'end' : 'middle',
                  }}
                  height={data.length > 6 ? 55 : 30}
                  interval={0}
                />
                <YAxis
                  tickFormatter={v => `${v.toFixed(1)}%`}
                  tick={{ fontSize: 11 }}
                  width={48}
                />
                <Tooltip content={<ProfitTooltip />} />
                <Bar dataKey="profit_rate" name="수익률" maxBarSize={20} radius={[2, 2, 0, 0]}>
                  {data.map((d, i) => (
                    <Cell key={i} fill={d.profit_rate >= 0 ? '#e74c3c' : '#3498db'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    );
  };

  // ════════════════════════════════════════════════════════
  // 패널 ④: 현금흐름 (매도손익 + 분배금 + 이자 + 순현금흐름선)
  // ════════════════════════════════════════════════════════

  const renderCashflow = () => {
    const data = cashflowData?.data ?? [];

    return (
      <div className="gp-panel">
        <div className="gp-panel-header">
          <span className="gp-panel-title">④ 현금흐름(매도손익+분배금+이자)</span>
          <div className="gp-toggle-group">
            {CASHFLOW_PERIODS.map(({ months, label }) => (
              <button
                key={months}
                className={`gp-toggle-btn ${cashflowMonths === months ? 'active' : ''}`}
                onClick={() => handleCashflowMonthsChange(months)}
              >{label}</button>
            ))}
          </div>
        </div>
        <div className="gp-panel-body">
          {!cashflowData ? (
            <div className="gp-empty">조회 버튼을 클릭하세요.</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={data}
                barCategoryGap="25%"
                margin={{ top: 8, right: 16, left: 4, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="year_month"
                  tickFormatter={ym => ym.slice(5)}
                  tick={{ fontSize: 10 }}
                  interval={Math.max(0, Math.floor(data.length / 13) - 1)}
                />
                <YAxis tickFormatter={fY} tick={{ fontSize: 11 }} width={44} />
                <Tooltip content={<CashflowTooltip />} />
                <Legend
                  iconSize={10}
                  wrapperStyle={{ fontSize: 11 }}
                  content={() => (
                    <div style={{ display: 'flex', gap: 12, justifyContent: 'center', fontSize: 11, paddingTop: 4 }}>
                      <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#FFA500', marginRight: 3 }} />분배금</span>
                      <span>
                        <span style={{ display: 'inline-block', width: 10, height: 10, background: '#FF4444', marginRight: 3 }} />
                        <span style={{ display: 'inline-block', width: 10, height: 10, background: '#4444FF', marginRight: 3 }} />
                        매도손익
                      </span>
                      <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#00AA00', marginRight: 3 }} />이자</span>
                      <span><span style={{ display: 'inline-block', width: 20, height: 2, background: '#f5a623', marginRight: 3, verticalAlign: 'middle' }} />순현금흐름</span>
                    </div>
                  )}
                />
                <Bar dataKey="dividend" name="분배금" fill="#FFA500" maxBarSize={12} radius={[2, 2, 0, 0]} />
                <Bar dataKey="sell_profit" name="매도손익" maxBarSize={12} radius={[2, 2, 0, 0]}>
                  {data.map((d, i) => (
                    <Cell
                      key={i}
                      fill={d.sell_profit > 0 ? '#FF4444' : d.sell_profit < 0 ? '#4444FF' : '#AAAAAA'}
                    />
                  ))}
                </Bar>
                <Bar dataKey="interest" name="이자" fill="#00AA00" maxBarSize={12} radius={[2, 2, 0, 0]} />
                <Line
                  dataKey="net_cashflow"
                  name="순현금흐름"
                  stroke="#f5a623"
                  strokeWidth={2}
                  dot={false}
                  type="monotone"
                />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    );
  };

  // ════════════════════════════════════════════════════════
  // 패널 ③: 목표 vs 현재 투자 비율 (세로 막대)
  // ════════════════════════════════════════════════════════

  const renderPlanRatio = () => {
    const items = planView === 'sector'
      ? planData?.by_sector ?? []
      : planData?.by_stock ?? [];

    return (
      <div className="gp-panel">
        <div className="gp-panel-header">
          <span className="gp-panel-title">③ 목표 vs 현재 투자 비율(매수원금 기준)</span>
          <div className="gp-toggle-group">
            <button
              className={`gp-toggle-btn ${planView === 'sector' ? 'active' : ''}`}
              onClick={() => setPlanView('sector')}
            >섹터별</button>
            <button
              className={`gp-toggle-btn ${planView === 'stock' ? 'active' : ''}`}
              onClick={() => setPlanView('stock')}
            >종목별</button>
          </div>
        </div>
        <div className="gp-panel-body">
          {!planData ? (
            <div className="gp-empty">조회 버튼을 클릭하세요.</div>
          ) : items.length === 0 ? (
            <div className="gp-empty">데이터가 없습니다.</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={items}
                barCategoryGap="30%"
                margin={{ top: 8, right: 16, left: 0, bottom: items.length > 8 ? 50 : 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="name"
                  tick={{
                    fontSize: 10,
                    angle: items.length > 6 ? -40 : 0,
                    textAnchor: items.length > 6 ? 'end' : 'middle',
                  }}
                  height={items.length > 6 ? 55 : 30}
                  interval={0}
                />
                <YAxis
                  tickFormatter={v => `${v}%`}
                  tick={{ fontSize: 11 }}
                  width={40}
                  domain={[0, 'auto']}
                />
                <Tooltip content={<RatioTooltip />} />
                <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="plan_ratio"    name="목표비율"              fill="#b0c4de" maxBarSize={16} radius={[2, 2, 0, 0]} />
                <Bar dataKey="current_ratio" name="현재 투자비율(매수원금)" fill="#4a90e2" maxBarSize={16} radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    );
  };

  // ════════════════════════════════════════════════════════
  // 포트폴리오 상세 팝업
  // ════════════════════════════════════════════════════════

  const renderPiePopup = () => {
    if (!piePopup.open) return null;
    const isProfitPopup = piePopup.type === 'profit';
    const netProfit = portfolioData?.net_profit ?? 0;

    return (
      <div
        className="gp-popup-overlay"
        onClick={() => setPiePopup(p => ({ ...p, open: false }))}
      >
        <div className="gp-popup-box" onClick={e => e.stopPropagation()}>
          <div className="gp-popup-title">
            <span>{isProfitPopup ? '예상손익 종목 목록' : `${piePopup.name} 종목 목록`}</span>
            <button
              className="gp-popup-close"
              onClick={() => setPiePopup(p => ({ ...p, open: false }))}
            >✕</button>
          </div>
          <div className="gp-popup-body">
            {piePopup.loading ? (
              <div className="gp-empty" style={{ height: 120 }}>조회 중…</div>
            ) : piePopup.data.length === 0 ? (
              <div className="gp-empty" style={{ height: 120 }}>데이터가 없습니다.</div>
            ) : isProfitPopup ? (
              <table className="gp-popup-table">
                <thead>
                  <tr>
                    <th>종목명</th>
                    <th className="gp-col-r">예상손익</th>
                    <th className="gp-col-r">비율</th>
                  </tr>
                </thead>
                <tbody>
                  {piePopup.data.map((row, i) => (
                    <tr key={i}>
                      <td>{row.name}</td>
                      <td
                        className="gp-col-r"
                        style={{ color: row.is_profit ? '#cc0000' : '#0000cc', fontWeight: 600 }}
                      >
                        {row.is_profit ? '+' : ''}{fNum(Math.round(row.profit_loss))}원
                      </td>
                      <td className="gp-col-r">{fRate(row.ratio)}%</td>
                    </tr>
                  ))}
                  <tr style={{ fontWeight: 700, background: '#f5f7ff' }}>
                    <td>합계 (순예상손익)</td>
                    <td
                      className="gp-col-r"
                      style={{ color: netProfit >= 0 ? '#cc0000' : '#0000cc' }}
                    >
                      {netProfit >= 0 ? '+' : ''}{fNum(Math.round(netProfit))}원
                    </td>
                    <td className="gp-col-r">—</td>
                  </tr>
                </tbody>
              </table>
            ) : (
              <table className="gp-popup-table">
                <thead>
                  <tr>
                    <th>종목명</th>
                    <th>티커</th>
                    <th>계좌</th>
                    <th className="gp-col-r">매수금액</th>
                    <th className="gp-col-r">비율</th>
                    <th className="gp-col-r">현재수익률</th>
                  </tr>
                </thead>
                <tbody>
                  {piePopup.data.map((row, i) => (
                    <tr key={i}>
                      <td>{row.stock_name}</td>
                      <td className="gp-col-ticker">{row.ticker}</td>
                      <td>{row.account_name}</td>
                      <td className="gp-col-r">{fNum(row.buy_amount)}원</td>
                      <td className="gp-col-r">{fRate(row.ratio)}%</td>
                      <td
                        className="gp-col-r"
                        style={{ color: row.profit_rate >= 0 ? '#cc0000' : '#0000cc', fontWeight: 600 }}
                      >
                        {fRate(row.profit_rate)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ── 렌더 ─────────────────────────────────────────────────

  return (
    <PageLayout title="그래프(실시간)" toolbar={toolbar} message={message}>
      <div className="gp-grid">
        {renderPortfolio()}
        {renderProfitRate()}
        {renderPlanRatio()}
        {renderCashflow()}
      </div>
      {renderPiePopup()}
    </PageLayout>
  );
};

export default GraphPage;
